// src/pages/Chat/NewGroupModal.jsx
import React, { useState, useEffect, useRef } from "react";
import { FiX, FiSearch, FiLoader, FiUsers, FiCheck } from "react-icons/fi";
import { resolvePhotoUrl } from "../../utils/photoHelper";
import apiClient from "../../api/apiClient";
import { createGroupConversation, getKeyBundle } from "../../api/chatService";
import useChatStore from "../../store/chatStore";
import { ChatCrypto } from "../../utils/crypto";

const SEARCH_URL = "/accounts/api/users/";

const GRADIENTS = [
  "from-violet-600 to-blue-600",
  "from-blue-600 to-cyan-500",
  "from-emerald-600 to-teal-500",
  "from-rose-600 to-pink-500",
  "from-amber-500 to-orange-500",
  "from-indigo-600 to-purple-600",
];

function getStringHash(str = "") {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash);
}

function getGradient(str) {
  const hash = getStringHash(str);
  return GRADIENTS[hash % GRADIENTS.length];
}

function getInitials(name = "") {
  return name.split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";
}

function UserAvatar({ photo, name, employeeId }) {
  const [imgErr, setImgErr] = useState(false);
  const resolvedPhoto = resolvePhotoUrl(photo);

  if (resolvedPhoto && !imgErr) {
    return (
      <img
        src={resolvedPhoto}
        alt={name}
        className="w-full h-full object-cover"
        onError={() => setImgErr(true)}
      />
    );
  }
  return (
    <div className={`w-full h-full bg-gradient-to-br ${getGradient(employeeId)} flex items-center justify-center text-white text-xs font-semibold select-none`}>
      {getInitials(name)}
    </div>
  );
}

function normaliseUser(u) {
  const firstName = u.first_name || "";
  const lastName  = u.last_name  || "";
  const fullName  =
    u.full_name ||
    (firstName || lastName ? `${firstName} ${lastName}`.trim() : "") ||
    u.username ||
    u.employee_id ||
    "Unknown";

  return {
    employee_id: u.employee_id || u.id || "",
    full_name:   fullName,
    role:        u.role        || u.designation || "",
    department:  u.department  || u.dept        || "",
    photo:       u.photo       || u.profile_photo || u.avatar || null,
  };
}

export default function NewGroupModal({ onClose, currentUserId }) {
  const [step,      setStep]      = useState(1); // 1: Select Members, 2: Group Info
  const [query,     setQuery]     = useState("");
  const [allUsers,  setAllUsers]  = useState([]);
  const [results,   setResults]   = useState([]);
  const [selected,  setSelected]  = useState([]); // employee_ids
  const [groupName, setGroupName] = useState("");
  const [loading,   setLoading]   = useState(false);
  const [creating,  setCreating]  = useState(false);
  const [error,     setError]     = useState(null);

  const inputRef = useRef(null);
  const upsertConversation = useChatStore((s) => s.upsertConversation);
  const selectConversation = useChatStore((s) => s.selectConversation);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const { data } = await apiClient.get(SEARCH_URL, { params: { all_for_chat: "true" } });
        const raw = Array.isArray(data) ? data : (data.users || data.results || []);
        const normalised = raw
          .map(normaliseUser)
          .filter((u) => u.employee_id && u.employee_id !== currentUserId);
        setAllUsers(normalised);
        setResults(normalised);
      } catch (err) {
        setError("Failed to load users.");
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [currentUserId]);

  useEffect(() => {
    const q = query.toLowerCase().trim();
    if (!q) {
      setResults(allUsers);
    } else {
      setResults(allUsers.filter(u => 
        u.full_name.toLowerCase().includes(q) || 
        u.employee_id.toLowerCase().includes(q)
      ));
    }
  }, [query, allUsers]);

  const toggleSelect = (userId) => {
    setSelected(prev => 
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const handleCreate = async () => {
    if (!groupName.trim()) {
      setError("Please enter a group name.");
      return;
    }
    setCreating(true);
    setError(null);

    try {
      // 1. Generate Conversation Key
      const convKey = window.crypto.getRandomValues(new Uint8Array(32));
      const convKeyBase64 = btoa(String.fromCharCode(...convKey));
      const myKeyPair = await ChatCrypto.getIdentityKeyPair();

      // 2. Encrypt for each member (including self)
      const encryptedKeys = [];
      
      const generateMemberKey = async (targetEid, targetPubKey, bundleVersion = 1) => {
        const salt = window.crypto.getRandomValues(new Uint8Array(16));
        const saltB64 = ChatCrypto.arrayBufferToBase64(salt);
        const shared = await ChatCrypto.deriveSharedKey(myKeyPair.privateKey, targetPubKey, saltB64);
        const enc = await ChatCrypto.encrypt(convKeyBase64, shared);
        return {
          member_employee_id: targetEid,
          encrypted_key:      enc.ciphertext,
          key_iv:             enc.iv,
          hkdf_salt:          saltB64,
          key_bundle_version: bundleVersion
        };
      };

      // Add self
      encryptedKeys.push(await generateMemberKey(currentUserId, myKeyPair.publicKey, 1));

      // Add others
      const membersToFetch = selected;
      for (const eid of membersToFetch) {
        try {
          const { data: bundle } = await getKeyBundle(eid);
          if (bundle && bundle.identity_public_key) {
            const theirPubKey = await ChatCrypto.importPublicKey(bundle.identity_public_key);
            encryptedKeys.push(await generateMemberKey(eid, theirPubKey, bundle.rotation_count || 1));
          }
        } catch (e) {
          console.warn(`[Crypto] Skipping E2EE for ${eid} (no keys found)`);
        }
      }

      // 3. API Call
      const { data } = await createGroupConversation(groupName, selected, encryptedKeys);
      // Note: Backend createGroupConversation might need to be updated to accept encrypted_keys if it doesn't already
      // But looking at services.py, it DOES accept it.
      
      upsertConversation(data);
      selectConversation(data.id);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to create group.");
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed z-[71] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-sm px-4">
        <div className="bg-slate-900 border border-white/10 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[80vh]">
          
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/8 shrink-0">
            <span className="text-sm font-semibold text-slate-100">
              {step === 1 ? "New Group: Select Members" : "New Group: Details"}
            </span>
            <button onClick={onClose} className="p-1.5 rounded-lg text-slate-500 hover:text-slate-300 hover:bg-white/10 transition-colors">
              <FiX size={15} />
            </button>
          </div>

          {step === 1 ? (
            <>
              {/* Search */}
              <div className="px-4 py-3 border-b border-white/8 shrink-0">
                <div className="relative">
                  <FiSearch size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                  <input
                    ref={inputRef}
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search people..."
                    className="w-full pl-8 pr-3 py-2 rounded-lg text-sm bg-white/5 border border-white/10 text-slate-200 placeholder-slate-600 outline-none focus:border-blue-500/50 transition-colors"
                  />
                </div>
              </div>

              {/* Selected Count / Next */}
              <div className="px-4 py-2 bg-blue-500/5 border-b border-white/5 flex items-center justify-between shrink-0">
                <span className="text-[10px] uppercase font-bold text-slate-500">
                  {selected.length} members selected
                </span>
                <button
                  onClick={() => selected.length > 0 && setStep(2)}
                  disabled={selected.length === 0}
                  className="px-3 py-1 rounded-md bg-blue-600 text-white text-xs font-bold disabled:opacity-30 transition-opacity"
                >
                  Next
                </button>
              </div>

              {/* User List */}
              <div className="flex-1 overflow-y-auto" style={{ scrollbarWidth: "thin" }}>
                {loading && allUsers.length === 0 && (
                  <div className="flex flex-col items-center py-12 gap-2 text-slate-500">
                    <FiLoader className="animate-spin" />
                    <span className="text-xs">Loading people...</span>
                  </div>
                )}
                {results.map(user => (
                  <button
                    key={user.employee_id}
                    type="button"
                    onClick={() => toggleSelect(user.employee_id)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 transition-colors text-left border-l-4 ${
                      selected.includes(user.employee_id) 
                        ? "bg-blue-600/10 border-blue-500" 
                        : "hover:bg-white/5 border-transparent"
                    }`}
                  >
                    <div className="w-9 h-9 rounded-full shrink-0 relative overflow-hidden flex items-center justify-center text-xs font-bold shadow-md">
                      <UserAvatar photo={user.photo} name={user.full_name} employeeId={user.employee_id} />
                      {selected.includes(user.employee_id) && (
                        <div className="absolute inset-0 bg-blue-500/60 flex items-center justify-center">
                          <FiCheck className="text-white" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-200 truncate font-medium">{user.full_name}</p>
                      <p className="text-[10px] text-slate-500 truncate">{user.employee_id} • {user.role || user.department}</p>
                    </div>
                  </button>
                ))}
              </div>
            </>
          ) : (
            <div className="p-4 flex flex-col gap-4">
              <div>
                <label className="text-[10px] uppercase font-bold text-slate-500 mb-1 block">Group Name</label>
                <input
                  autoFocus
                  type="text"
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  placeholder="Enter group name..."
                  className="w-full px-3 py-2 rounded-lg text-sm bg-white/5 border border-white/10 text-slate-200 outline-none focus:border-blue-500/50 transition-colors"
                />
              </div>

              {error && <p className="text-xs text-red-400">{error}</p>}

              <div className="flex gap-2">
                <button
                  onClick={() => setStep(1)}
                  className="flex-1 py-2 rounded-lg bg-white/5 text-slate-300 text-sm font-bold hover:bg-white/10 transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={handleCreate}
                  disabled={creating || !groupName.trim()}
                  className="flex-[2] py-2 rounded-lg bg-blue-600 text-white text-sm font-bold hover:bg-blue-700 disabled:opacity-40 transition-all flex items-center justify-center gap-2"
                >
                  {creating ? <FiLoader className="animate-spin" /> : <FiUsers />}
                  Create Group
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
